import React from 'react';
import {
    DefaultButton, Dropdown, IconButton, IDropdownOption, Panel, PanelType, Separator, Stack, TextField, Toggle,
} from '@fluentui/react';
import { IStorefrontSettings } from '../../../../models/Storefront';
import { ISearchElementStyle } from '../searchPageConfig';
import { ElementPropertyRows, StyleOverridesGroup, StylePartAccordions } from '../styleControls';
import { styleTargetForKey } from '../stylesConfig';
import { DynamicSetting } from '../../../Common/Setting';
import {
    DISMISS_MODE_LABELS, INotificationBar, INotificationsConfig, NOTIFICATION_PART_LABELS,
    NOTIFICATION_PART_TOOLTIPS, NOTIFICATION_STYLE_PARTS, NotificationDismissMode, NotificationStylePart,
    orderedBars, parseNotificationsConfig, scheduleStateOf,
} from '../notificationsConfig';

// The Notification Bars editor: the scheduled, dismissable bands that sit above the header and push
// it (and the page) down until a shopper closes them.
//
// It lives in its own flyout off the Header & Footer tab rather than inline on the preview, for the
// same reason the suggestions panel does: four style parts plus content, a schedule and a dismissal
// rule is far more than fits beside a header mock-up. The bars are chrome shared by every page, which
// is why this tab owns them.

const genBarId = (): string =>
    (typeof crypto !== 'undefined' && 'randomUUID' in crypto)
        ? crypto.randomUUID()
        : `notif-${Date.now()}-${Math.random().toString(36).slice(2)}`;

// Which style part each bar field maps to for the named-Storefront-Style picker. Keys are the
// STYLE_TARGET_BY_KEY names, kept beside the parts they belong to so the two can't drift.
const STYLE_KEY_BY_PART: Record<NotificationStylePart, string> = {
    bar: 'notificationBar',
    message: 'notificationMessage',
    link: 'notificationLink',
    closeButton: 'notificationCloseButton',
};

const DISMISS_OPTIONS: IDropdownOption[] = (['none', 'session', 'persistent'] as NotificationDismissMode[])
    .map((k) => ({ key: k, text: DISMISS_MODE_LABELS[k] }));

const SCHEDULE_BADGES: Record<string, { text: string; color: string }> = {
    live: { text: 'Live', color: '#107c10' },
    scheduled: { text: 'Scheduled', color: '#8661c5' },
    expired: { text: 'Expired', color: '#a19f9d' },
    disabled: { text: 'Off', color: '#a19f9d' },
};

// <input type="datetime-local"> speaks local wall-clock time with no zone; the config stores UTC
// instants. These two convert across that boundary, so a merchant in Chicago typing 9:00 gets a bar
// that starts at 9:00 for them and the right instant for everyone else.
function isoToLocalInput(iso?: string): string {
    if (!iso) return '';
    const ms = Date.parse(iso);
    if (Number.isNaN(ms)) return '';
    const d = new Date(ms);
    const pad = (n: number) => `${n}`.padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function localInputToIso(local?: string): string | undefined {
    if (!local) return undefined;
    const ms = Date.parse(local);
    // A half-typed date ("2026-08") parses to something meaningless or NaN. Treat it as "not set yet"
    // rather than writing a bogus instant that would silently hide the bar.
    return Number.isNaN(ms) ? undefined : new Date(ms).toISOString();
}

interface IStorefrontNotificationsEditorProps {
    isOpen: boolean;
    onDismiss: () => void;
    storefrontSettings: IStorefrontSettings;
    onStorefrontSettingsChange: (newSettings: IStorefrontSettings) => void;
}

const StorefrontNotificationsEditor: React.FC<IStorefrontNotificationsEditorProps> = (props) => {
    const config: INotificationsConfig = React.useMemo(
        () => parseNotificationsConfig(props.storefrontSettings.notificationsConfig),
        [props.storefrontSettings.notificationsConfig]);
    const bars = orderedBars(config);

    const [selectedId, setSelectedId] = React.useState<string | undefined>(undefined);
    const selected = bars.find((b) => b.id === selectedId) ?? bars[0];

    // One clock for the badges, so a panel that sits open doesn't reshuffle its labels under the
    // merchant's cursor. Re-read whenever the panel is reopened.
    const now = React.useMemo(() => Date.now(), [props.isOpen]);

    // A bar's revision revives it for shoppers who dismissed an earlier wording, so it must move when
    // the copy changes — but bumping on every keystroke would turn one edit into fifty. Bump at most
    // once per bar per time the panel is open; reopening starts a fresh editing session.
    const bumped = React.useRef<Set<string>>(new Set());
    React.useEffect(() => { if (props.isOpen) bumped.current = new Set(); }, [props.isOpen]);

    const writeBars = (next: INotificationBar[]) => {
        props.onStorefrontSettingsChange({
            ...props.storefrontSettings,
            notificationsConfig: JSON.stringify({ ...config, bars: next }),
        });
    };

    /**
     * Patch one bar. `contentChanged` marks edits shoppers can SEE (the message, the call to action) —
     * those bump the revision so anyone who dismissed the old wording gets the new one. Styling,
     * scheduling and the admin-only name deliberately don't: re-nagging every shopper over a color
     * change would make the feature hostile to use.
     */
    const updateBar = (id: string, patch: Partial<INotificationBar>, contentChanged?: boolean) => {
        // Read from the live settings rather than the memo above, so quick successive edits across
        // fields can't clobber one another.
        const current = orderedBars(parseNotificationsConfig(props.storefrontSettings.notificationsConfig));
        writeBars(current.map((b) => {
            if (b.id !== id) return b;
            const next = { ...b, ...patch };
            if (contentChanged && !bumped.current.has(id)) {
                bumped.current.add(id);
                next.revision = (b.revision ?? 0) + 1;
            }
            return next;
        }));
    };

    const addBar = () => {
        const bar: INotificationBar = {
            id: genBarId(),
            name: `Notification ${bars.length + 1}`,
            text: '',
            // `session` is the default the config documents: shoppers get the bar back next visit,
            // which is the least surprising of the three and the easiest to recover from.
            dismissMode: 'session',
            sortOrder: bars.length,
        };
        writeBars([...bars, bar]);
        setSelectedId(bar.id);
    };

    const removeBar = (id: string) => {
        writeBars(bars.filter((b) => b.id !== id).map((b, i) => ({ ...b, sortOrder: i })));
        if (selectedId === id) setSelectedId(undefined);
    };

    const moveBar = (id: string, delta: number) => {
        const from = bars.findIndex((b) => b.id === id);
        const to = from + delta;
        if (from < 0 || to < 0 || to >= bars.length) return;
        const next = [...bars];
        [next[from], next[to]] = [next[to], next[from]];
        writeBars(next.map((b, i) => ({ ...b, sortOrder: i })));
    };

    const updatePart = (part: NotificationStylePart, patch: Partial<ISearchElementStyle>) => {
        if (!selected) return;
        updateBar(selected.id, { [part]: { ...selected[part], ...patch } });
    };

    return (
        <Panel
            isOpen={props.isOpen}
            onDismiss={props.onDismiss}
            headerText='Notification Bars'
            type={PanelType.medium}
            isBlocking={false}
            closeButtonAriaLabel='Close'
        >
            <p style={{ margin: '0 0 0.75em', fontSize: 12, color: '#605e5c' }}>
                Full-width bars above the header. They push the header and the page down while they
                show, and the page moves back up when a shopper closes one. Bars appear on every page.
            </p>

            <Stack tokens={{ childrenGap: 4 }}>
                {bars.map((bar, i) => {
                    const badge = SCHEDULE_BADGES[scheduleStateOf(bar, now)];
                    const isSelected = selected?.id === bar.id;
                    return (
                        <Stack
                            key={bar.id}
                            horizontal
                            verticalAlign='center'
                            tokens={{ childrenGap: 6 }}
                            styles={{ root: {
                                padding: '4px 6px',
                                borderRadius: 4,
                                cursor: 'pointer',
                                border: `1px solid ${isSelected ? '#0078d4' : '#edebe9'}`,
                                background: isSelected ? '#f3f9fd' : '#fff',
                            } }}
                            onClick={() => setSelectedId(bar.id)}
                        >
                            <span style={{ flexGrow: 1, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {bar.name || bar.text || 'Untitled bar'}
                            </span>
                            <span style={{ fontSize: 11, color: badge.color, fontWeight: 600 }}>{badge.text}</span>
                            <IconButton iconProps={{ iconName: 'ChevronUp' }} title='Move up' disabled={i === 0} onClick={(e) => { e.stopPropagation(); moveBar(bar.id, -1); }} />
                            <IconButton iconProps={{ iconName: 'ChevronDown' }} title='Move down' disabled={i === bars.length - 1} onClick={(e) => { e.stopPropagation(); moveBar(bar.id, 1); }} />
                            <IconButton iconProps={{ iconName: 'Delete' }} title='Delete' onClick={(e) => { e.stopPropagation(); removeBar(bar.id); }} />
                        </Stack>
                    );
                })}
                <DefaultButton text='Add bar' iconProps={{ iconName: 'Add' }} onClick={addBar} styles={{ root: { marginTop: 4 } }} />
            </Stack>

            {selected && (
                <div>
                    <Separator styles={{ root: { marginTop: 16 } }} />
                    <Stack tokens={{ childrenGap: 8 }}>
                        <DynamicSetting id={`storefront.notificationBar.${selected.id}.name`}>
                            <TextField
                                label='Name'
                                description='Shown here only, so you can tell your bars apart.'
                                value={selected.name ?? ''}
                                onChange={(_, v) => updateBar(selected.id, { name: v || undefined })}
                            />
                        </DynamicSetting>
                        <DynamicSetting id={`storefront.notificationBar.${selected.id}.text`}>
                            <TextField
                                label='Message'
                                multiline
                                autoAdjustHeight
                                value={selected.text ?? ''}
                                onChange={(_, v) => updateBar(selected.id, { text: v || undefined }, true)}
                            />
                        </DynamicSetting>
                        <Stack horizontal tokens={{ childrenGap: 8 }}>
                            <DynamicSetting id={`storefront.notificationBar.${selected.id}.linkText`}>
                                <TextField
                                    label='Link text'
                                    styles={{ root: { flexGrow: 1 } }}
                                    value={selected.linkText ?? ''}
                                    onChange={(_, v) => updateBar(selected.id, { linkText: v || undefined }, true)}
                                />
                            </DynamicSetting>
                            <DynamicSetting id={`storefront.notificationBar.${selected.id}.linkUrl`}>
                                <TextField
                                    label='Link URL'
                                    styles={{ root: { flexGrow: 1 } }}
                                    value={selected.linkUrl ?? ''}
                                    onChange={(_, v) => updateBar(selected.id, { linkUrl: v || undefined }, true)}
                                />
                            </DynamicSetting>
                        </Stack>
                        <DynamicSetting id={`storefront.notificationBar.${selected.id}.newTab`}>
                            <Toggle
                                inlineLabel
                                label='Open link in a new tab'
                                checked={selected.newTab ?? false}
                                onChange={(_, checked) => updateBar(selected.id, { newTab: checked || undefined }, true)}
                                styles={{ root: { marginBottom: 0 } }}
                            />
                        </DynamicSetting>

                        <Stack horizontal tokens={{ childrenGap: 8 }}>
                            <DynamicSetting id={`storefront.notificationBar.${selected.id}.startsAt`}>
                                <TextField
                                    label='Starts'
                                    type='datetime-local'
                                    styles={{ root: { flexGrow: 1 } }}
                                    value={isoToLocalInput(selected.startsAt)}
                                    onChange={(_, v) => updateBar(selected.id, { startsAt: localInputToIso(v) })}
                                />
                            </DynamicSetting>
                            <DynamicSetting id={`storefront.notificationBar.${selected.id}.endsAt`}>
                                <TextField
                                    label='Ends'
                                    type='datetime-local'
                                    styles={{ root: { flexGrow: 1 } }}
                                    value={isoToLocalInput(selected.endsAt)}
                                    onChange={(_, v) => updateBar(selected.id, { endsAt: localInputToIso(v) })}
                                />
                            </DynamicSetting>
                        </Stack>
                        <div style={{ fontSize: 11, color: '#605e5c', marginTop: -4 }}>
                            Your local time. Leave Starts empty to show it right away, and Ends empty so it never expires.
                        </div>

                        <DynamicSetting id={`storefront.notificationBar.${selected.id}.dismissMode`}>
                            <Dropdown
                                label='When a shopper closes it, keep it closed'
                                options={DISMISS_OPTIONS}
                                selectedKey={selected.dismissMode ?? 'session'}
                                onChange={(_, o) => updateBar(selected.id, { dismissMode: o?.key as NotificationDismissMode })}
                            />
                        </DynamicSetting>
                        <div style={{ fontSize: 11, color: '#605e5c', marginTop: -4 }}>
                            Editing the message brings the bar back for shoppers who already closed it. Changing its
                            styling or schedule does not.
                        </div>

                        <DynamicSetting id={`storefront.notificationBar.${selected.id}.disabled`}>
                            <Toggle
                                inlineLabel
                                label='Enabled'
                                checked={!selected.disabled}
                                onChange={(_, checked) => updateBar(selected.id, { disabled: checked ? undefined : true })}
                                styles={{ root: { marginBottom: 0 } }}
                            />
                        </DynamicSetting>
                    </Stack>

                    <StylePartAccordions
                        key={selected.id}
                        startCollapsed
                        parts={NOTIFICATION_STYLE_PARTS.map((part) => ({
                            key: part,
                            label: NOTIFICATION_PART_LABELS[part],
                            tooltip: NOTIFICATION_PART_TOOLTIPS[part],
                            content: (
                                <StyleOverridesGroup
                                    rowLayout
                                    target={styleTargetForKey(STYLE_KEY_BY_PART[part])}
                                    value={selected[part]}
                                    onChange={(patch) => updatePart(part, patch)}
                                >
                                    <ElementPropertyRows settingKeyPrefix={`storefront.notifications.${part}`}
                                        value={selected[part]}
                                        onChange={(patch) => updatePart(part, patch)}
                                    />
                                </StyleOverridesGroup>
                            ),
                        }))}
                    />
                </div>
            )}
        </Panel>
    );
};

export default StorefrontNotificationsEditor;
